import React, { useState } from 'react'
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'

import { Icon, IconButton, type SemanticIconName } from '~components/internal'

import './ButtonTabs.scss'

export interface ButtonTabOption {
  label: string
  value: string | number
  icon?: SemanticIconName
}

export interface ButtonTabMenuActions {
  onRename?: (value: string | number) => void
  onDelete?: (value: string | number) => void
}

export interface ButtonTabsProps {
  options: ButtonTabOption[]
  value: string | number
  onChange: (value: string | number) => void
  onAdd?: () => void
  showAddButton?: boolean
  tabMenuActions?: ButtonTabMenuActions
}

const ButtonTabs: React.FC<ButtonTabsProps> = ({
  options,
  value,
  onChange,
  onAdd,
  showAddButton = false,
  tabMenuActions,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [activeMenuOption, setActiveMenuOption] = useState<string | number | null>(null)

  const handleChevronClick = (
    e: React.MouseEvent<HTMLSpanElement>,
    optionValue: string | number,
  ): void => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
    setActiveMenuOption(optionValue)
  }

  const handleMenuClose = (): void => {
    setMenuAnchor(null)
    setActiveMenuOption(null)
  }

  const handleRename = (): void => {
    if (activeMenuOption !== null) tabMenuActions?.onRename?.(activeMenuOption)
    handleMenuClose()
  }

  const handleDelete = (): void => {
    if (activeMenuOption !== null) tabMenuActions?.onDelete?.(activeMenuOption)
    handleMenuClose()
  }

  return (
    <div className='internal-button-tabs'>
      {options.map((option) => {
        const isSelected = value === option.value
        const isMenuOpen = activeMenuOption === option.value
        const tabClassName = [
          'internal-button-tabs__tab',
          isSelected && 'internal-button-tabs__tab--selected',
          isMenuOpen && 'internal-button-tabs__tab--menu-open',
          tabMenuActions && 'internal-button-tabs__tab--has-menu',
        ]
          .filter(Boolean)
          .join(' ')

        return (
          <button
            key={option.value}
            className={tabClassName}
            onClick={() => onChange(option.value)}
            type='button'
          >
            {tabMenuActions && (
              <span className='internal-button-tabs__spacer' aria-hidden>
                <Icon name='expandMore' fontSize='small' />
              </span>
            )}
            {option.icon && <Icon name={option.icon} fontSize='small' />}
            {option.label}
            {tabMenuActions && (
              <span
                className='internal-button-tabs__chevron'
                role='button'
                tabIndex={-1}
                onClick={(e) => handleChevronClick(e, option.value)}
              >
                <Icon name='expandMore' fontSize='small' />
              </span>
            )}
          </button>
        )
      })}
      {showAddButton && onAdd && (
        <IconButton
          className='internal-button-tabs__add-button'
          aria-label='Add tab'
          onClick={onAdd}
        >
          <Icon name='addCircle' fontSize='small' />
        </IconButton>
      )}
      {tabMenuActions && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {tabMenuActions.onRename && (
            <MenuItem dense onClick={handleRename}>
              <ListItemIcon>
                <Icon name='edit' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Rename</ListItemText>
            </MenuItem>
          )}
          {tabMenuActions.onRename && tabMenuActions.onDelete && <Divider />}
          {tabMenuActions.onDelete && (
            <MenuItem dense onClick={handleDelete} className='destructive'>
              <ListItemIcon>
                <Icon name='delete' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      )}
    </div>
  )
}

export default ButtonTabs
